import { checkAccessForNavigation } from '@/helpers/routeAccessValidation.js'
import { getInstanceField } from '@/hooks/useGetInstanceField'
import { useState } from '@/hooks/vue-utils/useVuex'
import type { RawLocation } from 'vue-router'

/**
 * Whether the current user is allowed to navigate to a given route.
 *
 * Gate hyperlinks on this. The global navigation guard (`App.vue` →
 * `setNavigationGuard`) bounces a disallowed navigation back to the root with a
 * "no access" toast, so an ungated link is worse than no link at all — a
 * `manager` role, for instance, may read users but reaches neither Groups,
 * Product nor Events.
 *
 * The target is resolved through the router and handed to the same validator
 * the guard runs, rather than re-deriving role and feature rules here. That
 * keeps link visibility and the guard in step by construction: route meta the
 * guard honours (`requiresPortal`, `isMaster`, `abacResource`, …) is picked up
 * automatically, including on records added later.
 */
export const useCanNavigateTo = () => {
  const router = getInstanceField('$router')
  const ability = getInstanceField('$ability')

  const { currentUser, currentCompany } = useState('common', [
    'currentUser',
    'currentCompany',
  ])

  const canNavigateTo = (location: RawLocation): boolean => {
    if (!router || !ability) return false

    // Permissions land on the store before the app renders any route, so an
    // absent user or company means we cannot yet tell — stay closed.
    if (!currentUser.value || !currentCompany.value) return false

    const { route } = router.resolve(location)

    return checkAccessForNavigation(
      currentCompany.value,
      currentUser.value,
      route,
      ability
    )
  }

  return { canNavigateTo }
}
